import { getAuthInstance } from '@fusion/auth-v3'
import { getLogInAsHeaders, getLogInAsUrl, LogInAsHeaderNames } from '@fusion/saas-components-v3'
import { UnitOfMeasure } from '@fusion/units'
import { getQueryExpression } from '@/services/data-export'
import { ContentType } from './utils'
import { GET } from './helpers'

type OnProgressFn = (completed?: boolean, bytesReceived?: number) => void

export const getCSV = async function (
  devId: string,
  start: string,
  end: string,
  permissions: string[],
  measurements: string[],
  signal: AbortController['signal'],
  isShared = false,
  tempUnit = UnitOfMeasure.DEGREES_C,
  pressureUnit = UnitOfMeasure.INCH_MERCURY,
  pmUnit = UnitOfMeasure.MICROGRAMS_PER_CUBIC_METER,
  onProgressFn?: OnProgressFn
) {
  const queryExpression = getQueryExpression(start, end)
  const clientOffset = getClientOffset()
  const temp = tempUnit === UnitOfMeasure.DEGREES_F ? 'f' : 'c'

  if (typeof onProgressFn === 'function') {
    // start progress
    onProgressFn()
  }

  const result = await GET(
    `/devices/${devId}/telemetry?${queryExpression}&tempunit=${temp}&pressureunit=${pressureUnit}&pmunit=${pmUnit}`,
    {
      Accept: ContentType.CSV,
      'X-Client-Offset': clientOffset,
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID])
    },
    { signal },
    APP_ENV.VITE_APP_DEVICES_HOST_V3
  )
  return downloadCSV(
    result,
    {
      filter: true,
      permissions,
      supportedMeasurements: measurements,
      isShared
    },
    onProgressFn
  )
}
export const getNotificationsCSV = async function (start: string, end: string) {
  const queryExpression = getQueryExpression(start, end)
  const auth = getAuthInstance()
  const res = await GET(
    getLogInAsUrl(`/accounts/accountId/notifications/export?${queryExpression}`, auth),
    {
      Accept: ContentType.CSV,
      ...getLogInAsHeaders([LogInAsHeaderNames.ACCOUNT_ID, LogInAsHeaderNames.USER_ID])
    },
    {},
    APP_ENV.VITE_APP_ALERT_HOST
  )
  return downloadCSV(res)
}

const getClientOffset = () => new Date().getTimezoneOffset()

const downloadCSV = async (
  res: Response,
  options: Record<string, unknown> | null = null,
  onProgressFn?: OnProgressFn
) => {
  const progress = typeof onProgressFn === 'function' ? onProgressFn : () => {}

  const title = res.headers.get('Content-Disposition').split('=')[1].split('"')[1]

  // This is required to get the name of the latest downloaded file for Cypress tests
  const exportDataDialog = document.getElementsByClassName('export-data-dialog')
  if (exportDataDialog?.length) {
    exportDataDialog[0].setAttribute('data-downloaded-file-name', title)
  }

  const reader = res.body.getReader()
  const chunks = []

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    chunks.push(value)

    // progress
    progress(false, value.length)
  }

  const blob = new Blob(chunks, { type: 'text/csv;charset=utf-8' })
  const elem = window.document.createElement('a')
  elem.href = window.URL.createObjectURL(blob)
  elem.download = title
  document.body.appendChild(elem)
  elem.click()
  document.body.removeChild(elem)

  // complete
  progress(true)
}
